import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { TextField, IconButton } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import DatePickerDialog from './datePickerDialog';
import { format, parse, isValid, parseISO } from 'date-fns';
import { usePreferences } from 'hooks/usePreferences';
import { mapPreferenceFormatToDateFns } from 'utils/dateFormatUtils';

function SimpleDatePicker({ 
    label, 
    onChange, 
    value, 
    size = 'small', 
    error = null, 
    helperText = null, 
    margin = 'dense', 
    disabled = false,
    minDate = null,
    maxDate = null,
    helperTextMode = 'always' // 'always' | 'onError' | 'never'
}) {
    const [internalValue, setInternalValue] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [datePickerDialogOpen, setDatePickerDialogOpen] = useState(false);
    const [inputError, setInputError] = useState(false);
    const { preferences } = usePreferences();

    // Map preference format to date-fns format
    const dateFormat = mapPreferenceFormatToDateFns(preferences?.dateFormat || 'YYYY-MM-DD');
    const displayFormat = dateFormat; // Use same format for display as configured in preferences

    // Safely parse a date string
    const safeParseDateString = useCallback((dateString) => {
        if (!dateString) return null;
        
        try {
            // Try ISO format first (YYYY-MM-DD)
            const isoDate = parseISO(dateString);
            if (isValid(isoDate)) return isoDate;
            
            // Try parsing with expected format
            const parsedDate = parse(dateString, dateFormat, new Date());
            if (isValid(parsedDate)) return parsedDate;
            
            return null;
        } catch {
            return null;
        }
    }, [dateFormat]);

    // Get display value for the input
    const displayValue = useMemo(() => {
        const date = safeParseDateString(internalValue);
        return date ? format(date, displayFormat) : inputValue;
    }, [internalValue, inputValue, safeParseDateString, displayFormat]);

    // Sync with external value prop
    useEffect(() => {
        if (value === null || value === undefined || value === '') {
            setInternalValue('');
            setInputValue('');
            setInputError(false);
        } else {
            const date = safeParseDateString(value);
            if (date) {
                const formattedValue = format(date, dateFormat);
                setInternalValue(formattedValue);
                setInputValue(format(date, displayFormat));
                setInputError(false);
            } else {
                setInternalValue('');
                setInputValue(value); // Keep invalid input visible
                setInputError(true);
            }
        }
    }, [value, safeParseDateString, dateFormat, displayFormat]);

    // Validate date against min/max constraints
    const validateDateConstraints = useCallback((date) => {
        if (!date || !isValid(date)) return false;
        
        if (minDate) {
            const min = safeParseDateString(minDate);
            if (min && date < min) return false;
        }
        
        if (maxDate) {
            const max = safeParseDateString(maxDate);
            if (max && date > max) return false;
        }
        
        return true;
    }, [minDate, maxDate, safeParseDateString]);

    const handleClickOpen = useCallback(() => {
        if (!disabled) {
            setDatePickerDialogOpen(true);
        }
    }, [disabled]);

    const handleInputChange = useCallback((e) => {
        const input = e.target.value;
        setInputValue(input);
        
        if (!input.trim()) {
            // Handle clearing
            setInternalValue('');
            setInputError(false);
            onChange?.(null);
            return;
        }

        // Try to parse various formats
        const date = safeParseDateString(input) || 
                    parse(input, displayFormat, new Date()) ||
                    parse(input, 'MM/dd/yyyy', new Date()) ||
                    parse(input, 'M/d/yyyy', new Date());

        if (isValid(date)) {
            if (validateDateConstraints(date)) {
                const formattedDate = format(date, dateFormat);
                setInternalValue(formattedDate);
                setInputError(false);
                onChange?.(formattedDate);
            } else {
                setInputError(true);
            }
        } else {
            setInputError(true);
        }
    }, [safeParseDateString, displayFormat, dateFormat, validateDateConstraints, onChange]);

    const handleDatePickerDone = useCallback((date) => {
        setDatePickerDialogOpen(false);
        
        if (!date || !isValid(date)) {
            return;
        }

        if (validateDateConstraints(date)) {
            const formattedDate = format(date, dateFormat);
            setInternalValue(formattedDate);
            setInputValue(format(date, displayFormat));
            setInputError(false);
            onChange?.(formattedDate);
        }
    }, [dateFormat, displayFormat, validateDateConstraints, onChange]);

    const handleBlur = useCallback(() => {
        // On blur, if we have a valid internal value, show it properly formatted
        if (internalValue && !inputError) {
            const date = safeParseDateString(internalValue);
            if (date) {
                setInputValue(format(date, displayFormat));
            }
        }
    }, [internalValue, inputError, safeParseDateString, displayFormat]);

    // Calculate effective error state and helper text
    const effectiveError = error || inputError;
    const computedHelperText = useMemo(() => {
        if (inputError) {
            if (minDate && maxDate) {
                return `Please enter a valid date between ${minDate} and ${maxDate}`;
            } else if (minDate) {
                return `Please enter a valid date after ${minDate}`;
            } else if (maxDate) {
                return `Please enter a valid date before ${maxDate}`;
            }
            return 'Please enter a valid date (YYYY-MM-DD)';
        }
        return helperText || `Format: ${preferences.dateFormat || 'YYYY-MM-DD'} or use calendar picker`;
    }, [inputError, minDate, maxDate, helperText, preferences.dateFormat]);

    const shouldShowHelperText = (helperTextMode === 'always') || (helperTextMode === 'onError' && effectiveError);
    const effectiveHelperText = shouldShowHelperText ? computedHelperText : undefined;

    return (
        <>
            <TextField
                label={label || "Select Date"}
                value={displayValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                size={size}
                error={effectiveError}
                helperText={effectiveHelperText}
                disabled={disabled}
                placeholder="YYYY-MM-DD"
                InputProps={{
                    endAdornment: (
                        <IconButton 
                            onClick={handleClickOpen} 
                            disabled={disabled}
                            aria-label="Open calendar"
                        >
                            <CalendarToday />
                        </IconButton>
                    ),
                }}
                fullWidth
                margin={margin}
                aria-label={`${label || 'Date'} input`}
                role="textbox"
                aria-haspopup="dialog"
                inputProps={{
                    'aria-describedby': effectiveHelperText ? `${label}-helper-text` : undefined
                }}
            />
            <DatePickerDialog
                open={datePickerDialogOpen}
                onClose={() => setDatePickerDialogOpen(false)}
                onDone={handleDatePickerDone}
                date={internalValue}
                minDate={minDate}
                maxDate={maxDate}
            />
        </>
    );
}

export default SimpleDatePicker;